import {throwError as observableThrowError, Observable, of, forkJoin } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
  HttpHandler,
  HttpEvent,
  HttpContextToken,
  HttpContext,
  HttpStatusCode,
} from "@angular/common/http";
import { ClientNotificationService } from "../client-notification.service";
import { TranslocoService } from "@jsverse/transloco";
import { UserInfoService } from '../user-info.service';
import { UserService } from '../../../swaggerServices';
import { serializeBodyAsString } from '../../shared/util/request';

// answer trace errors are handled manually, we should probably use a service for this
// where other components/services can register to handle their own errors
const skipUrl = url => /answer\/\d+\/trace/.test(url) || /subscribe/.test(url) || /processState\/runCalcs\/taxReturn\/\d+/.test(url);

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
  constructor(
    private notificationService: ClientNotificationService,
    private translocoService: TranslocoService,
    private userInfoService: UserInfoService,
    private userService: UserService,
  ) {}
  // show popup error messages on all error status except for 401 unauthorised which will show a login modal instead
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request.clone()).pipe(catchError((error) => {
      // Don't attempt to handle errors with sending the notification email, because that can potentially
      // result in an infinite loop of errors and email send requests
      if (!(error instanceof HttpErrorResponse) || request.context.get(ERROR_NOTIFICATION_MAIL_REQUEST)) {
        return observableThrowError(error);
      }

      // Error emails get sent regardless of the status, the value of the `noalert` flag, and the `skipUrl` flag value
      this.sendErrorEmail(error, request);

      // No-op for 401/Unauthorized because there is global handling to show a login modal
      if (error.status === HttpStatusCode.Unauthorized || skipUrl(request.url) || error.error?.noalert) {
        return observableThrowError(error);
      }

      const getErrorMessageObservable = request.context.get(ERROR_MESSAGE_HANDLER);
      const messageTitle = "Server Error"

      if (!error.error?.message) {
        const messageObservable = getErrorMessageObservable?.(error);

        if (messageObservable) {
          messageObservable.subscribe((message) => {
            this.notificationService.appendMessage(messageTitle, message);
          });
        } else {
          this.getErrorMessageString(error).subscribe((messages) => {
            this.notificationService.appendMessage(messageTitle, messages);
          });
        }
      }

      return observableThrowError(error);
    }));
  }

  private getErrorMessageString(error: HttpErrorResponse): Observable<[string, string]> {
    // Modern browsers will return a 0 status code if the HTTP request was not successfully completed.
    // This implies a network error which can include user network issues, CORS errors, interference by firewalls, etc.
    // see fetch/XMLHttpRequest spec: https://fetch.spec.whatwg.org/#concept-network-error
    if (error.error instanceof ProgressEvent && error.status === 0) {
      return this.translocoService.selectTranslate('response-errors.network')
    }

    switch (error.status) {
      case HttpStatusCode.Forbidden:
        return forkJoin<[string, string]>([
          this.translocoService.selectTranslate<string>('response-errors.permission', {}, 'ja'),
          this.translocoService.selectTranslate<string>('response-errors.permission', {}, 'en'),
        ]);
      case HttpStatusCode.BadRequest:
      case HttpStatusCode.UnprocessableEntity:
      case HttpStatusCode.NotFound:
        return forkJoin<[string, string]>([
          this.translocoService.selectTranslate('response-errors.invalid', {}, 'ja'),
          this.translocoService.selectTranslate('response-errors.invalid', {}, 'en'),
        ]);
      case HttpStatusCode.InternalServerError:
      case HttpStatusCode.ServiceUnavailable:
      case HttpStatusCode.BadGateway:
      case HttpStatusCode.GatewayTimeout:
        return forkJoin<[string, string]>([
          this.translocoService.selectTranslate('response-errors.server', {}, 'ja'),
          this.translocoService.selectTranslate('response-errors.server', {}, 'en'),
        ]);
      default:
        return forkJoin<[string, string]>([
          this.translocoService.selectTranslate('response-errors.fallback-message', {}, 'ja'),
          this.translocoService.selectTranslate('response-errors.fallback-message', {}, 'en'),
        ]);
    }
  }

  /**
   * Error responses sent back from the server will usually include additional
   * json with message details. This helper narrows the type down to an object
   * that contains a `message` property
   */
  private isMessageObject(val: unknown): val is { message: string } {
    return typeof val === 'object' && typeof val['message'] === 'string';
  }

  private async sendErrorEmail(error: HttpErrorResponse, request: HttpRequest<any>) {
    const tokenObject = await this.userInfoService.getAdToken();
    const accountInfo = tokenObject?.account;

    if (!tokenObject || !accountInfo) {
      console.debug("Missing AD token or AD account info. Aborted sending of error notification email");
      return;
    }

    // Use performance over Date.now for time information since it's not affected
    // by the user changing the time on their system
    const currentTime = new Date(performance.timeOrigin + performance.now());
    const errorDetails = [
      `Status: ${error.status}`,
      `StatusText: ${error.statusText}`,
      `Message: ${error.message}`,
      `Url: ${error.url}`,
      `Time: ${currentTime.toString()}`,
      `Request Method: ${request.method}`,
      `Request Body: ${serializeBodyAsString(request)}`,
      `Request Params: ${request.params.toString() || "(no request parameters)"}`,
    ];

    const details = !error.error
      ? null
      : this.isMessageObject(error.error)
        ? error.error.message
        : String(error.error);

    // Ensure a string like '[object Object]' is filtered out
    if (details && details !== String({})) {
      errorDetails.push(`Details: ${details}`);
    }

    const plainTextBody = errorDetails.join("\n");
    const subject = `[Speedcube] ${error.status} response from ${error.url}`;

    const { accessToken: adToken } = tokenObject;
    const { username } = accountInfo;

    const notificationData = {
      adToken,
      subject,
      text: plainTextBody,
      from: username,
    };

    // Encode data to help prevent payload being blocked by the WAF
    const stringifiedData = JSON.stringify(notificationData)
    const notificationAsFile = new Blob([stringifiedData], { type: "text/plain" });

    this.userService
      .notifyOnUserBehalf(
        notificationAsFile,
        'body',
        false,
        { context: new HttpContext().set(ERROR_NOTIFICATION_MAIL_REQUEST, true) },
      )
      .subscribe()
  }
}

// The transloco `selectTranslate` function returns an observable, so the type Observable is used
// as the return value for of the handler
type ErrorMessageHandler = (response: HttpErrorResponse) => Observable<string> | null;

/**
 * Context token that allows for passing a custom handler via HTTPContext that can be used
 * to come up with an error message in cases where a server provided error message is not available
 */
export const ERROR_MESSAGE_HANDLER = new HttpContextToken<ErrorMessageHandler | null>(() => null);

/**
 * Used to signal to this intercepter that it shouldn't attempt to handle errors with sending
 * notification emails about error responses that were returned by other requests.
 * This is to prevent a situation where an infinite loop of errors occurs due to network issues, etc.
 */
const ERROR_NOTIFICATION_MAIL_REQUEST = new HttpContextToken<boolean>(() => false);
